import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import BullMark from "@/components/BullMark";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { projectConfig, buyTarget } from "@/config/project";

const LINKS = [
  ["home", "#home", "nav-link-home"],
  ["about", "#about", "nav-link-about"],
  ["tokenomics", "#tokenomics", "nav-link-tokenomics"],
  ["howToBuy", "#how-to-buy", "nav-link-how-to-buy"],
  ["contract", "#contract", "nav-link-contract"],
  ["faq", "#faq", "nav-link-faq"],
  ["community", "#community", "nav-link-community"],
];

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const buyProps = buyTarget.external
    ? { href: buyTarget.href, target: "_blank", rel: "noopener noreferrer" }
    : { href: buyTarget.href };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-red-900/30 bg-[#050203]/85 backdrop-blur-md" : "border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8" aria-label="Main">
        <a href="#home" className="flex items-center gap-2.5" data-testid="nav-logo" aria-label="SLN 杀零牛 — home">
          <BullMark size={34} />
          <span className="leading-none">
            <span className="block font-zh text-lg font-black text-zinc-50">{projectConfig.chineseName}</span>
            <span className="block font-mono text-[10px] tracking-[0.3em] text-yellow-500">{projectConfig.symbol}</span>
          </span>
        </a>

        <ul className="hidden items-center gap-6 lg:flex">
          {LINKS.map(([key, href, id]) => (
            <li key={key}>
              <a
                href={href}
                data-testid={id}
                className="text-xs font-medium uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-yellow-400 focus-visible:text-yellow-400"
              >
                {t(`nav.${key}`)}
              </a>
            </li>
          ))}
          <li>
            <a
              href={projectConfig.whitepaperUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-link-whitepaper"
              className="text-xs font-medium uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-yellow-400 focus-visible:text-yellow-400"
            >
              {t("nav.whitepaper")}
            </a>
          </li>
        </ul>

        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-1 rounded-full border border-red-900/40 p-1 sm:flex" data-testid="language-switcher" role="group" aria-label="Language">
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                aria-pressed={lang === l.code}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  lang === l.code ? "bg-red-700 text-white" : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            {...buyProps}
            data-testid="nav-buy-sln-button"
            className="hidden rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 px-5 py-2 text-xs font-bold tracking-[0.15em] text-black shadow-[0_0_24px_rgba(234,179,8,0.35)] transition-transform hover:-translate-y-0.5 sm:inline-block"
          >
            {t("nav.buy")}
          </a>

          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t("nav.close") : t("nav.menu")}
            className="rounded-md p-2 text-zinc-200 hover:bg-red-950/50 lg:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-red-900/30 bg-[#050203]/95 backdrop-blur-md lg:hidden"
            data-testid="mobile-menu"
          >
            <ul className="space-y-1 px-4 py-4">
              {LINKS.map(([key, href, id]) => (
                <li key={key}>
                  <a
                    href={href}
                    data-testid={`mobile-${id}`}
                    onClick={() => setOpen(false)}
                    className="block rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-[0.15em] text-zinc-300 hover:bg-red-950/40 hover:text-yellow-400"
                  >
                    {t(`nav.${key}`)}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={projectConfig.whitepaperUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-link-whitepaper"
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-[0.15em] text-zinc-300 hover:bg-red-950/40 hover:text-yellow-400"
                >
                  {t("nav.whitepaper")}
                </a>
              </li>
              <li className="flex items-center gap-2 px-3 pt-3">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    aria-pressed={lang === l.code}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                      lang === l.code ? "bg-red-700 text-white" : "border border-red-900/40 text-zinc-400"
                    }`}
                  >
                    {l.name}
                  </button>
                ))}
              </li>
              <li className="px-3 pt-3">
                <a
                  {...buyProps}
                  data-testid="mobile-nav-buy-sln-button"
                  onClick={() => setOpen(false)}
                  className="block rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 px-5 py-3 text-center text-sm font-bold tracking-[0.15em] text-black"
                >
                  {t("nav.buy")}
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
